// Two ways of asking a Google service for JSON, because one of them is not always allowed.
//
// The browser's usual `fetch` is governed by CORS: the service has to send a header saying
// our site may read the answer. Google Apps Script normally does, but not in every account
// configuration, and the Sheets "gviz" endpoint never does. Loading the answer through a
// <script> tag instead sidesteps CORS entirely, because a script tag may be loaded from
// anywhere — which is why these services offer to wrap their JSON in a function call for us.
//
// `fetchJson` is tried first everywhere; `jsonp` is the fallback.

export const DEFAULT_TIMEOUT_MS = 8000;

export async function fetchJson(url, options = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { cache: "no-store", ...options, signal: controller.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

let seq = 0;
const uniqueName = () => `__apmercl${Date.now().toString(36)}${(seq += 1)}`;

/**
 * Loads `url` as a script, having asked it to call a one-off global function with its JSON.
 *
 * `build` receives the generated function name and returns the address to load, because the
 * two services disagree about how to be told: Apps Script wants `?callback=name`, gviz wants
 * it buried inside its `tqx` parameter.
 */
export function jsonp(build, timeoutMs = DEFAULT_TIMEOUT_MS) {
  return new Promise((resolve, reject) => {
    const name = uniqueName();
    const script = document.createElement("script");
    let settled = false;

    const cleanup = () => {
      settled = true;
      clearTimeout(timer);
      try {
        delete window[name];
      } catch {
        window[name] = undefined;
      }
      script.remove();
    };

    const timer = setTimeout(() => {
      if (settled) return;
      cleanup();
      reject(new Error("timed out"));
    }, timeoutMs);

    window[name] = (data) => {
      if (settled) return;
      cleanup();
      resolve(data);
    };

    script.onerror = () => {
      if (settled) return;
      cleanup();
      reject(new Error("unreachable"));
    };

    script.src = build(name);
    document.head.appendChild(script);
  });
}

/** The Apps Script spelling: append `&callback=...` to a URL that may already have a query. */
export const appsScriptJsonp = (url, timeoutMs) =>
  jsonp((name) => `${url}${url.includes("?") ? "&" : "?"}callback=${name}`, timeoutMs);
